/**
 * TransactionTwinLinks (#1624-E) — the "Verknüpfte Twin-Elemente" block in a
 * Liquiditätsplanung ledger-row detail. Reads the #1624-D transaction↔twin
 * links for one transaction and lists them (harvest crop_cycle for the
 * Deckungsbeitrag join; Bodenprobe / lab documents for the lab-payment join).
 *
 * Mounted ONLY for the expanded row (see LedgerFeed.RowDetail) so the read
 * query fires once for the open transaction, not once per visible ledger row.
 * Renders nothing while loading / on error / when there are no links, so it
 * never adds noise to an unlinked transaction.
 */
import { useLiquiditaetLinks } from '../../../hooks/useTwin';
import type { LiquiditaetTwinLink } from '../../../services/twinService';
import './TransactionTwinLinks.css';

const TWIN_ENTITY_LABELS: Record<string, string> = {
  crop_cycle: 'Kultur / Ernte',
  field: 'Schlag',
  parcel: 'Flurstück',
  organization: 'Organisation',
};

const LINK_ROLE_LABELS: Record<string, string> = {
  harvest: 'Ernte',
  soil_test: 'Bodenprobe',
  lab_payment: 'Laborzahlung',
  lab: 'Labor',
};

export interface TwinLinkDescription {
  label: string;
  detail: string | null;
}

/**
 * Human label + secondary detail for one link. Entity links lead with the
 * twin-type (German); document links lead with the role ("Bodenprobe") or a
 * generic "Dokument". The note is preferred as the detail line, falling back
 * to the role label for entity links so a harvest link is never bare.
 */
export function describeTwinLink(link: LiquiditaetTwinLink): TwinLinkDescription {
  const roleLabel = link.link_role ? LINK_ROLE_LABELS[link.link_role] ?? link.link_role : null;
  if (link.target_kind === 'entity') {
    const typeLabel = TWIN_ENTITY_LABELS[link.target_entity_type ?? ''] ?? link.target_entity_type ?? 'Twin-Element';
    return { label: typeLabel, detail: link.note ?? roleLabel };
  }
  return { label: roleLabel ?? 'Dokument', detail: link.note ?? null };
}

export default function TransactionTwinLinks({ transactionId }: { transactionId: string }) {
  const { data } = useLiquiditaetLinks({ mode: 'transaction', transaction_id: transactionId });
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <div className="liquiditaetsplanung-twinlinks">
      <span className="liquiditaetsplanung-twinlinks__title">Verknüpfte Twin-Elemente</span>
      <ul className="liquiditaetsplanung-twinlinks__list">
        {items.map((link) => {
          const { label, detail } = describeTwinLink(link);
          return (
            <li key={link.derived_fact_id} className="liquiditaetsplanung-twinlinks__item">
              <span className="liquiditaetsplanung-twinlinks__label">{label}</span>
              {detail && <span className="liquiditaetsplanung-twinlinks__detail">{detail}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
